import {
  paymentStatus,
  paymentStatusEQF,
  EQF,
  EXP,
  TUC,
  failurePaymentStatuses,
} from 'types/creditReport.constants';
import { isNumber } from './number';
import {
  MAX_YEARS_TO_SHOW_PAYMENT_HISTORY,
  MIN_YEARS_TO_SHOW_PAYMENT_HISTORY,
} from 'pages_CreditReportTU_constants/creditReportTU.constants';

/**
 * TU Credit Report - reduceUpTo is going to reduce the data
 * for every account's payment history having as a pivot the
 * first year in the position 0 and the last failure payment
 * in the array, in case we didn't have any failure,
 * it should apply a limitation for 2 years ago */

const reduceUpTo = params => {
  const { dataArray, minimumYears, maximumYears } = params;
  const currentDataArray = dataArray?.filter(payment =>
    isNumber(payment?.year),
  );
  let pivotYear = parseInt(currentDataArray[0]?.year);
  let yearPosition = false;
  let yearsCounter = false;
  currentDataArray?.forEach((payment, index) => {
    if (yearsCounter < maximumYears) {
      const isFailurePayment =
        failurePaymentStatuses.includes(payment[TUC]) ||
        failurePaymentStatuses.includes(payment[EQF]) ||
        failurePaymentStatuses.includes(payment[EXP]);
      if (isFailurePayment) {
        yearsCounter = pivotYear - parseInt(payment?.year);
        yearPosition = index;
      }
    }
  });
  const minimumMonths = minimumYears * 12;
  if (yearPosition > minimumMonths) {
    return currentDataArray?.slice(0, yearPosition + 1);
  } else {
    return currentDataArray?.slice(0, minimumMonths);
  }
};

/**
 * TU Credit Report - prepareSplitHistoryData is copied from TU Sample logic
 * attached in PREM-3970, to re-arrange and format payment histories
 * based on the credit report
 */

export const prepareSplitHistoryData = report => {
  const usedGroups = [[TUC], [EQF], [EXP]];
  let categories = [];
  let data = {};

  usedGroups?.forEach(g => {
    let catalogPaymentStatus =
      EQF === String(g) ? paymentStatusEQF : paymentStatus;
    let historyValues = report['PaymentHistory-' + g];
    for (let k in historyValues) {
      if (k[1] !== 'Y') {
        let date = historyValues[[k.slice(0, 1), 'Y', k.slice(1)].join('')];
        if (date) {
          if (!data[date]) {
            data[date] = {};
          }
          data[date][g] =
            historyValues[k] && catalogPaymentStatus?.[historyValues[k]]
              ? catalogPaymentStatus?.[historyValues[k]]?.text
              : '';
        }
      }
    }
  });
  // now we need to create an array with the dates, and sort it
  let dataArray = [];
  for (let k in data) {
    let [month, year] = k.split('-');
    data[k]['month'] = month;
    data[k]['year'] = year;
    dataArray.push(data[k]);
  }

  dataArray.sort(
    (a, b) =>
      a.year +
      (a.month.length === 1 ? '0' + a.month : a.month) -
      (b.year + (b.month.length === 1 ? '0' + b.month : b.month)),
  );

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  // now that we have the data sorted, we can rebuild a proper split object
  // and the categories array
  data = {};

  dataArray.reverse(); // show newest payment history first
  dataArray = reduceUpTo({
    dataArray,
    minimumYears: MIN_YEARS_TO_SHOW_PAYMENT_HISTORY,
    maximumYears: MAX_YEARS_TO_SHOW_PAYMENT_HISTORY,
  }); // limit payment history to the first year where there is a payment failure
  usedGroups.forEach(g => (data[g] = []));
  dataArray.forEach(d => {
    categories.push({ title: months[d.month - 1] + ' ' + d.year });
    usedGroups.forEach(g => {
      data[g].push(d[g] || '');
    });
  });

  return { categories, data };
};

/**
 * Check if user doesnt really have credit history
 * @param score
 * @returns boolean
 */
export const isFirstTimeCreditUser = score =>
  score && (score === 0 || score === 1 || score === 4);
